import { createContext, useContext } from 'react';

import type { PanelContextValue } from '@/types/panels';

/**
 * Provided by PanelHost so panel children can:
 *   - read mode without prop drilling
 *   - open another panel (`openPanel`)
 *   - update their own props (`setProp`)
 *   - close themselves (`close`)
 *   - hand a camera pick back to the shell (`selectCamera`), which decides
 *     which camera surface that should land on
 *   - widen themselves over their right-hand neighbours (`expand`), and read
 *     whether that would change anything (`canExpand`)
 *   - widen themselves into free room at the end of the row (`widen`)
 *
 * The context is intentionally minimal — it stays referentially stable
 * across unrelated panel state changes (other panels opening/closing,
 * URL params updating, focus shifting). Consumer panels that need the
 * full open-panel list receive those as
 * direct props from PanelStage; they're not in ctx because injecting
 * them would invalidate every consumer on every state tick.
 */
export const PanelContext = createContext<PanelContextValue | null>(null);

export function usePanelContext(): PanelContextValue {
    const ctx = useContext(PanelContext);
    if (!ctx) {
        throw new Error('usePanelContext used outside of <PanelHost>');
    }
    return ctx;
}
